import React from 'react';
import PropTypes from 'prop-types';
import { connect, useDispatch } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { Dialog, DialogContent, IconButton } from '@material-ui/core';
import ChevronLeftIcon from '@material-ui/icons/ChevronLeft';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';
import GetAppIcon from '@material-ui/icons/GetApp';
import { format, isValid, parseISO } from 'date-fns';
import moment from 'moment';
import { closeDialog } from 'common/utils/dialog-utils';
import { updateUI } from 'redux/actions/ui';
import { URLS } from 'common/urls';
import '../../assets/scss/modals/image-details.scss';

// File Icons
import iconPdf from 'assets/images/icons_svg/icon-pdf.svg';
import iconCsv from 'assets/images/icons_svg/icon-csv.svg';
import iconDoc from 'assets/images/icons_svg/icon-doc.svg';
import iconDocx from 'assets/images/icons_svg/icon-docx.svg';
import iconFile from 'assets/images/icons_svg/icon-file.svg';
import iconMov from 'assets/images/icons_svg/icon-mov.svg';
import iconMp3 from 'assets/images/icons_svg/icon-mp3.svg';
import iconMp4 from 'assets/images/icons_svg/icon-mp4.svg';
import iconXls from 'assets/images/icons_svg/icon-xls.svg';

const fileIcons = {
    csv: iconCsv,
    doc: iconDoc,
    docx: iconDocx,
    mov: iconMov,
    mp3: iconMp3,
    mp4: iconMp4,
    pdf: iconPdf,
    xls: iconXls,
    default: iconFile
};

const getFileIconForDetails = (fileName) => {
    if (!fileName) {
        return iconFile;
    }
    const ext = fileName.split('.').pop()?.toLowerCase();
    return fileIcons[ext] || fileIcons.default;
};

const imageExtensions = new Set([
    'jpg',
    'jpeg',
    'png',
    'gif',
    'webp',
    'bmp',
    'tif',
    'tiff',
    'svg'
]);

const isImageLikeFile = (mimeType, fileName) => {
    if (mimeType?.startsWith('image/')) {
        return true;
    }
    if (!fileName) {
        return false;
    }
    const ext = fileName.split('.').pop()?.toLowerCase();
    return imageExtensions.has(ext);
};

/**
 * ImageDetailsDialog - Shows detailed view of a media library image
 *
 * Features:
 * - Large image preview with navigation arrows
 * - Associated entries list
 * - Image metadata (upload date, file name, type, size, dimensions)
 * - Download link
 * - Create Entry button
 */
const ImageDetailsDialog = ({
    open,
    onClose,
    image,
    onPrev,
    onNext,
    hasPrev,
    hasNext,
    history
}) => {
    const dispatch = useDispatch();
    if (!image) {
        return null;
    }

    const formatFileSize = (bytes) => {
        if (!bytes) {
            return 'Unknown';
        }
        if (bytes < 1024) {
            return `${bytes} B`;
        }
        if (bytes < 1024 * 1024) {
            return `${(bytes / 1024).toFixed(1)} KB`;
        }
        return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    };

    const formatDate = (dateString) => {
        if (!dateString) {
            return 'Unknown';
        }
        try {
            const parsedIso = parseISO(dateString);
            if (isValid(parsedIso)) {
                return format(parsedIso, "MMMM dd, yyyy 'at' h:mm a");
            }
            const parsedDate = new Date(dateString);
            if (isValid(parsedDate)) {
                return format(parsedDate, "MMMM dd, yyyy 'at' h:mm a");
            }
            const parsedMoment = moment.utc(dateString);
            if (parsedMoment.isValid()) {
                return parsedMoment.local().format('MMMM DD, YYYY [at] h:mm a');
            }
            return dateString;
        } catch {
            const parsedMoment = moment.utc(dateString);
            return parsedMoment.isValid()
                ? parsedMoment.local().format('MMMM DD, YYYY [at] h:mm a')
                : dateString;
        }
    };

    const getFileType = () => {
        if (image.file_mimetype) {
            return (
                image.file_mimetype.split('/')[1]?.toUpperCase() ||
                image.file_mimetype
            );
        }
        // Extract from file name
        const ext = image.file_name?.split('.').pop()?.toUpperCase();
        return ext || 'Unknown';
    };

    const getDimensions = () => {
        if (image.width && image.height) {
            return `${image.width} by ${image.height} pixels`;
        }
        const metaWidth = image?.meta_data?.width;
        const metaHeight = image?.meta_data?.height;
        if (metaWidth && metaHeight) {
            return `${metaWidth} by ${metaHeight} pixels`;
        }
        if (image?.meta_data?.dimensions) {
            return image.meta_data.dimensions;
        }
        return 'Unknown';
    };

    const getEntryTitle = (entry) => {
        console.log(entry, '  entry');
        return entry?.entry_title || entry?.title || entry?.entry?.title || '';
    };

    const getImageEntryTitle = () => {
        return (
            image?.entry_title ||
            image?.title ||
            image?.name ||
            'Untitled Entry'
        );
    };

    const handleDownload = () => {
        const link = document.createElement('a');
        const isImageType = image.file_mimetype?.startsWith('image/');
        link.href = isImageType
            ? image.xl_image || image.lg_image || image.image
            : image.file;
        link.download = image.file_name || 'file';
        link.target = '_blank';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const handleEntryClick = (entryId) => {
        if (entryId) {
            onClose();
            closeDialog();
            history.push(`/entry/${entryId}`);
        }
    };

    const handleCreateEntry = () => {
        const imageData = displayImage
            ? [
                  {
                      src: displayImage
                  }
              ]
            : [];
        dispatch(
            updateUI(
                {
                    newEntry: {
                        prefillMedia: imageData,
                        prefillMediaId: image?.id || null
                    }
                },
                false
            )
        );
        history.push(URLS.TIMELINE);
        onClose();
    };

    const displayImage =
        image.xl_image ||
        image.lg_image ||
        image.md_image ||
        image.image ||
        image.file_type_thumbnail;

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="lg"
            fullWidth
            className="image-details-dialog"
            PaperProps={{
                className: 'image-details-dialog-paper'
            }}>
            <DialogContent className="image-details-content">
                <div className="image-details-shell">
                    <div className="image-details-header">
                        <h2 className="image-details-title">Image Details</h2>
                        <div className="image-details-nav">
                            <IconButton
                                className="nav-button nav-prev"
                                onClick={onPrev}
                                aria-label="Previous image"
                                disabled={!hasPrev}>
                                <ChevronLeftIcon />
                            </IconButton>
                            <IconButton
                                className="nav-button nav-next"
                                onClick={onNext}
                                aria-label="Next image"
                                disabled={!hasNext}>
                                <ChevronRightIcon />
                            </IconButton>
                        </div>
                    </div>

                    <div className="image-preview-section">
                        <div
                            className={`image-preview-container ${
                                !isImageLikeFile(
                                    image.file_mimetype,
                                    image.file_name
                                )
                                    ? 'preview-container-file'
                                    : ''
                            }`}>
                            {isImageLikeFile(
                                image.file_mimetype,
                                image.file_name
                            ) ? (
                                <img
                                    src={displayImage}
                                    alt={image.title || 'Image preview'}
                                    className="preview-image"
                                />
                            ) : (
                                <div className="preview-file-container">
                                    <img
                                        src={
                                            image.file ||
                                            getFileIconForDetails(
                                                image.file_name
                                            )
                                        }
                                        alt={image.file_name || 'File preview'}
                                        className="preview-file-icon"
                                    />
                                </div>
                            )}
                        </div>
                    </div>

                    <div className="image-details-info">
                        {(image.associated_entries?.length > 0 ||
                            image.entry_id) && (
                            <div className="details-column">
                                <h3 className="section-title">
                                    Associated entries
                                </h3>
                                <div className="entries-list">
                                    {image.associated_entries?.length > 0 ? (
                                        image.associated_entries.map(
                                            (entry) => (
                                                <button
                                                    key={entry.id}
                                                    type="button"
                                                    className="entry-link"
                                                    onClick={() =>
                                                        handleEntryClick(
                                                            entry.id
                                                        )
                                                    }>
                                                    <span className="entry-title">
                                                        {getEntryTitle(entry)
                                                            ? `${getEntryTitle(
                                                                  entry
                                                              )} - `
                                                            : ''}
                                                        {formatDate(
                                                            entry.entry_date
                                                        )}
                                                    </span>
                                                </button>
                                            )
                                        )
                                    ) : (
                                        <button
                                            type="button"
                                            className="entry-link"
                                            onClick={() =>
                                                handleEntryClick(image.entry_id)
                                            }>
                                            <span className="entry-title">
                                                {getImageEntryTitle()}
                                            </span>
                                            <span className="entry-date">
                                                {formatDate(image.entry_date)}
                                            </span>
                                        </button>
                                    )}
                                </div>
                            </div>
                        )}

                        <div className="details-column">
                            <h3 className="section-title">Image Details</h3>
                            <div className="details-grid">
                                <div className="detail-row">
                                    <span className="detail-label">
                                        Uploaded on:
                                    </span>
                                    <span className="detail-value">
                                        {formatDate(image.created)}
                                    </span>
                                </div>
                                <div className="detail-row">
                                    <span className="detail-label">
                                        File name:
                                    </span>
                                    <span className="detail-value">
                                        {image.file_name || 'Unknown'}
                                    </span>
                                </div>
                                <div className="detail-row">
                                    <span className="detail-label">
                                        File type:
                                    </span>
                                    <span className="detail-value">
                                        {getFileType()}
                                    </span>
                                </div>
                                <div className="detail-row">
                                    <span className="detail-label">
                                        File size:
                                    </span>
                                    <span className="detail-value">
                                        {formatFileSize(image.file_size)}
                                    </span>
                                </div>
                                {image.file_mimetype?.startsWith('image/') && (
                                    <div className="detail-row">
                                        <span className="detail-label">
                                            Dimensions:
                                        </span>
                                        <span className="detail-value">
                                            {getDimensions()}
                                        </span>
                                    </div>
                                )}
                                {(image.location ||
                                    image.latitude ||
                                    image.longitude) && (
                                    <div className="detail-row">
                                        <span className="detail-label">
                                            Location:
                                        </span>
                                        <span className="detail-value">
                                            {image.location ||
                                                `${image.latitude}, ${image.longitude}`}
                                        </span>
                                    </div>
                                )}
                            </div>

                            <button
                                type="button"
                                className="download-link"
                                onClick={handleDownload}>
                                <GetAppIcon className="download-icon" />
                                Download Image
                            </button>
                        </div>
                    </div>

                    <div className="action-buttons">
                        <button
                            type="button"
                            className="btn-create-entry"
                            onClick={onClose}>
                            Close
                        </button>
                        <button
                            type="button"
                            className="btn-create-entry"
                            onClick={handleCreateEntry}>
                            Create Entry
                        </button>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
};

ImageDetailsDialog.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    image: PropTypes.shape({
        id: PropTypes.number,
        image: PropTypes.string,
        xs_image: PropTypes.string,
        md_image: PropTypes.string,
        lg_image: PropTypes.string,
        xl_image: PropTypes.string,
        file_type_thumbnail: PropTypes.string,
        width: PropTypes.number,
        height: PropTypes.number,
        entry_id: PropTypes.number,
        entry_date: PropTypes.string,
        associated_entries: PropTypes.arrayOf(
            PropTypes.shape({
                id: PropTypes.number,
                entry_date: PropTypes.string,
                title: PropTypes.string,
                entry_title: PropTypes.string
            })
        ),
        created: PropTypes.string,
        title: PropTypes.string,
        entry_title: PropTypes.string,
        file_name: PropTypes.string,
        file_mimetype: PropTypes.string,
        file_size: PropTypes.number
    }),
    onPrev: PropTypes.func,
    onNext: PropTypes.func,
    hasPrev: PropTypes.bool,
    hasNext: PropTypes.bool,
    history: PropTypes.object.isRequired
};

ImageDetailsDialog.defaultProps = {
    image: null,
    onPrev: () => {},
    onNext: () => {},
    hasPrev: false,
    hasNext: false
};

export default withRouter(connect()(ImageDetailsDialog));
